import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Segmento } from '@/components/kanban/triagem/segmentoTriagem';

export interface ResumoVendasDia {
  data: string;
  tipo_pessoa: Segmento | null;
  faturamento: number;
  venda_nova: number;
  migracao: number;
  vn_percent: number;
  mig_percent: number;
  altas: number;
  fibra: number;
  pn: number;
  tt: number;
  renovacao: number;
}

/**
 * O segmento de quem está logado, pela EQUIPE dele (`equipes.tipo_pessoa`).
 * Equipe CONECTA é PF; o resto da casa é PJ, que também é o padrão de quem não
 * tem equipe — o CRM foi CNPJ por cinco anos e o default não pode ser esconder
 * número de ninguém.
 */
export function useSegmentoDoUsuario(): Segmento {
  const { profile } = useAuth();
  const equipeId = profile?.equipe_id ?? null;

  const { data } = useQuery<Segmento>({
    queryKey: ['segmento-da-equipe', equipeId],
    enabled: !!equipeId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<Segmento> => {
      const { data: equipe, error } = await supabase
        .from('equipes')
        .select('tipo_pessoa')
        .eq('id', equipeId as string)
        .maybeSingle();
      if (error) throw error;
      return (equipe as { tipo_pessoa?: string } | null)?.tipo_pessoa === 'PF' ? 'PF' : 'PJ';
    },
  });

  return data ?? 'PJ';
}

/**
 * Resumo do dia (Altas, Fibra, PN, TT, Renovação, Faturamento) lido da RPC
 * `gerar_resumo_dia_venda` — a MESMA RPC que alimenta a mensagem automática
 * de "Venda Fechada". Usar essa RPC garante que o header do CRM mostra
 * exatamente os mesmos números do disparo, sem divergência.
 *
 * ⛔ CPF e CNPJ NUNCA somam (regra global 14/09/2026). Até 21/09/2026 este chip
 * chamava a RPC sem segmento e entregava PF + PJ no mesmo número — o mesmo furo
 * que fazia a venda CPF da Conecta entrar no aviso de venda fechada de um
 * pedido CNPJ. Agora cada um vê o SEU: a Conecta vê CPF, o resto vê CNPJ.
 */
export function useResumoVendasDia(segmento?: Segmento) {
  const doUsuario = useSegmentoDoUsuario();
  const tipoPessoa = segmento ?? doUsuario;

  return useQuery<ResumoVendasDia | null>({
    queryKey: ['resumo-vendas-dia', tipoPessoa],
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('gerar_resumo_dia_venda', {
        p_data: null,
        p_tipo_pessoa: tipoPessoa,
      } as never);
      if (error) throw error;
      return (data as unknown as ResumoVendasDia) ?? null;
    },
  });
}
